import React, { ChangeEvent } from "react";
import { FormikProps, FieldArrayRenderProps } from "formik";
import { formikFieldArrayProps } from "../../../framework/formUtils";

import { styled } from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import MuiSelect from "../../../framework/muiSelect";

import { handleApiError } from "../../../../services/baseService";
import { ProductForm } from "./productTable";
import { TransportOrderProductDetails } from "../../../../models/transport/transportOrderProductDetails";
import { ProductItem } from "../../../../models/product/product";
import { TransportOrderDetailsDto } from "../../../../models/transport/transportOrder";

export interface ProductRowsProps {
    formikProps: FormikProps<ProductForm>;
    arrayHelpers: FieldArrayRenderProps;
    changesDisabled?: boolean;
    ownerProducts: ProductItem[];
    order: TransportOrderDetailsDto;
    orderAddedProducts?: string[];
}

const StyledTextField = styled(TextField)(() => ({
    "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button": {
        "WebkitAppearance": "none",
        "margin": 0
    },
    "input[type=number]": {
        "MozAppearance": "textfield"
    }
}));

export const ProductRows = ({ formikProps, arrayHelpers, changesDisabled, ownerProducts, orderAddedProducts }: ProductRowsProps) => {
    const submit = () => void formikProps?.submitForm().catch(handleApiError);
    const handleTypeChange = (e: ChangeEvent<HTMLSelectElement>, i: number) => {
        void formikProps.setFieldValue(
            `rows.${i}.productId`,
            e.target.value
        );
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        setTimeout(() => submit());
    };

    const inputProps = {
        min: 0,
        style: {
            fontSize: "0.9rem",
        }
    };

    return (
        <>
            { formikProps?.values?.rows?.map((row: TransportOrderProductDetails, i: number) => {
                const selectedProduct = ownerProducts.find(p => p.id === row.productId);
                return (
                    <TableRow
                        key={`productTable-row-${row.key}`}
                        data-testid={`productTable-row-${i}`}
                    >
                        <TableCell sx={{ width: "5%" }}>
                            { i + 1 }
                        </TableCell>

                        <TableCell sx={{ width: "60%" }}>
                            <MuiSelect
                                {...formikFieldArrayProps(formikProps, "productId", i)}
                                options={
                                    ownerProducts.length > 0 &&
                                    ownerProducts?.map(product => ({ id: product.id, title: product.name }))
                                }
                                defaultValue=""
                                value={
                                    (row?.productId === undefined ||
                                    row?.productId === null ||
                                    ownerProducts.length === 0) ? "" : `${row?.productId}`
                                }
                                onChange={(e) => handleTypeChange(e, i)}
                                fontSize="0.9rem"
                                disabled={changesDisabled}
                                disabledItems={orderAddedProducts}
                            />
                        </TableCell>
                        <TableCell sx={{ width: "40%" }}>
                            <StyledTextField
                                {...formikFieldArrayProps(formikProps, "amount", i)}
                                type="number"
                                fullWidth
                                inputProps={inputProps}
                                disabled={changesDisabled}
                                InputProps={{
                                    endAdornment: selectedProduct?.unitName && (
                                        <InputAdornment position="end" sx={{ color: "text.secondary" }}>
                                            {selectedProduct.unitName}
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </TableCell>
                        <TableCell align="center">
                            <RemoveIcon
                                color={changesDisabled ? "disabled" : "primary"}
                                onClick={() => {
                                    if(!changesDisabled) {
                                        arrayHelpers.remove(i);
                                        submit();
                                    }
                                }}
                                data-testid={`productTable-row-delete-${i}`}
                            />
                        </TableCell>
                    </TableRow>
                );
            })}
        </>
    );
};
